import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import { filters } from "../filters";

const ALL_CITY_OPTION = {
    value: "All",
    label: "All",
};

const withAllOption = (options = []) => {
    const normalizedOptions = options.filter((option) => option?.label !== "All");
    return [ALL_CITY_OPTION, ...normalizedOptions];
};

const SearchCity = ({
    country,
    value,
    onChange,
    placeholder = "Search City",
    isClearable = true,
    limit = 50,
    minSearchLength = 2,
    debounceMs = 300,
}) => {
    const [cityOptions, setCityOptions] = useState([ALL_CITY_OPTION]);
    const [cityPage, setCityPage] = useState(0);
    const [citySearch, setCitySearch] = useState("");
    const [hasMoreCities, setHasMoreCities] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);

    const cityDebounceRef = useRef(null);
    const cityInFlightRef = useRef(false);
    const cityRequestKeyRef = useRef("");
    const fetchedKeysRef = useRef(new Set());

    const resetCityDropdown = () => {
        setCityOptions([ALL_CITY_OPTION]);
        setCityPage(0);
        setCitySearch("");
        setHasMoreCities(false);
        setIsLoadingCities(false);

        cityInFlightRef.current = false;
        cityRequestKeyRef.current = "";
        fetchedKeysRef.current = new Set();

        if (cityDebounceRef.current) {
            clearTimeout(cityDebounceRef.current);
            cityDebounceRef.current = null;
        }
    };

    const fetchCities = async ({ searchText, page, append }) => {
        const selectedCountry = country?.label;
        const normalizedSearchText = typeof searchText === "string" ? searchText.trim() : "";

        if (
            !selectedCountry ||
            cityInFlightRef.current ||
            (normalizedSearchText.length > 0 && normalizedSearchText.length < minSearchLength)
        ) {
            return;
        }

        const requestKey = `${selectedCountry}|${normalizedSearchText}|${page}`;
        if (fetchedKeysRef.current.has(requestKey)) {
            return;
        }

        fetchedKeysRef.current.add(requestKey);
        cityRequestKeyRef.current = requestKey;
        cityInFlightRef.current = true;
        setIsLoadingCities(true);

        try {
            const response = await filters.getCityList({
                country: selectedCountry,
                search: normalizedSearchText,
                limit,
                offset: page * limit,
            });

            if (cityRequestKeyRef.current !== requestKey) {
                return;
            }

            const cityData = Array.isArray(response?.data) ? response.data : [];
            const mappedOptions = cityData.map((city, index) => ({
                value: `${page}-${index}-${city}`,
                label: city,
            }));

            setCityOptions((prevOptions) => {
                if (!append) {
                    return withAllOption(mappedOptions);
                }

                const existingCities = new Set(prevOptions.map((item) => item.label));
                const uniqueNewOptions = mappedOptions.filter((item) => !existingCities.has(item.label));
                return withAllOption([...prevOptions, ...uniqueNewOptions]);
            });

            setCityPage(page);
            setHasMoreCities(cityData.length === limit);
        } catch (error) {
            fetchedKeysRef.current.delete(requestKey);
            if (!append) {
                setCityOptions([ALL_CITY_OPTION]);
            }
            setHasMoreCities(false);
            console.error("Error fetching cities:", error);
        } finally {
            cityInFlightRef.current = false;
            if (cityRequestKeyRef.current === requestKey) {
                setIsLoadingCities(false);
            }
        }
    };

    const handleCityInputChange = (inputValue, actionMeta) => {
        if (actionMeta.action !== "input-change") {
            return inputValue;
        }

        setCitySearch(inputValue);

        if (cityDebounceRef.current) {
            clearTimeout(cityDebounceRef.current);
            cityDebounceRef.current = null;
        }

        if (inputValue.length === 0) {
            setCityPage(0);
            setHasMoreCities(false);
            setIsLoadingCities(false);
            cityInFlightRef.current = false;
            fetchedKeysRef.current = new Set();

            if (country?.label) {
                fetchCities({ searchText: "", page: 0, append: false });
            } else {
                setCityOptions([ALL_CITY_OPTION]);
            }

            return inputValue;
        }

        if (inputValue.length < minSearchLength) {
            setCityOptions([ALL_CITY_OPTION]);
            setCityPage(0);
            setHasMoreCities(false);
            setIsLoadingCities(false);
            cityInFlightRef.current = false;
            fetchedKeysRef.current = new Set();
            return inputValue;
        }

        fetchedKeysRef.current = new Set();
        cityDebounceRef.current = setTimeout(() => {
            fetchCities({ searchText: inputValue, page: 0, append: false });
        }, debounceMs);

        return inputValue;
    };

    const handleCityMenuScrollToBottom = () => {
        if (
            !country?.label ||
            isLoadingCities ||
            !hasMoreCities ||
            (citySearch.length > 0 && citySearch.length < minSearchLength)
        ) {
            return;
        }

        fetchCities({ searchText: citySearch, page: cityPage + 1, append: true });
    };

    const handleMenuOpen = () => {
        const hasFetchedCityData = cityOptions.some((option) => option?.label !== "All");
        if (!country?.label || hasFetchedCityData || isLoadingCities) {
            return;
        }

        fetchCities({ searchText: citySearch, page: 0, append: false });
    };

    const handleChange = (selectedOption) => {
        setCitySearch("");

        if (
            selectedOption &&
            !cityOptions.some((option) => option.label === selectedOption.label)
        ) {
            setCityOptions((prevOptions) => [selectedOption, ...prevOptions]);
        }

        onChange(selectedOption);
    };

    useEffect(() => {
        resetCityDropdown();
    }, [country?.label]);

    useEffect(() => {
        return () => {
            if (cityDebounceRef.current) {
                clearTimeout(cityDebounceRef.current);
            }
        };
    }, []);

    return (
        <ReactSelect
            inputValue={citySearch}
            onInputChange={handleCityInputChange}
            onMenuOpen={handleMenuOpen}
            onMenuScrollToBottom={handleCityMenuScrollToBottom}
            options={cityOptions}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            isClearable={isClearable}
            isLoading={isLoadingCities}
            isDisabled={!country}
        />
    );
};

export default SearchCity;
